import { useEffect, useRef } from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";

interface UsePhotoPreloaderProps {
  photos: MediaItem[];
  currentIndex: number;
  enabled: boolean;
}

export const usePhotoPreloader = ({
  photos,
  currentIndex,
  enabled,
}: UsePhotoPreloaderProps) => {
  const preloadedImages = useRef<Map<number, HTMLImageElement>>(new Map());
  const preloadedIndices = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!enabled || photos.length === 0) return;

    // Preload next 2 and previous 1 images
    const targetIndices: number[] = [];

    // Previous image
    if (currentIndex - 1 >= 0) {
      targetIndices.push(currentIndex - 1);
    }

    // Next 2 images
    if (currentIndex + 1 < photos.length) {
      targetIndices.push(currentIndex + 1);
    }
    if (currentIndex + 2 < photos.length) {
      targetIndices.push(currentIndex + 2);
    }

    // Clean up images that are no longer in the preload window
    preloadedIndices.current.forEach((index) => {
      if (!targetIndices.includes(index)) {
        const img = preloadedImages.current.get(index);
        if (img) {
          img.src = "";
          preloadedImages.current.delete(index);
        }
        preloadedIndices.current.delete(index);
        console.log(`🧹 Cleaning up preload for photo at index ${index}`);
      }
    });

    // Preload new images
    targetIndices.forEach((index) => {
      // Skip if already preloaded at this index
      if (preloadedIndices.current.has(index)) {
        return;
      }

      const photo = photos[index];
      if (!photo) return;

      const imageUrl = photo.getUrlWithQueryAuth();
      if (!imageUrl) return;

      const img = new Image();
      img.src = imageUrl;
      preloadedImages.current.set(index, img);
      preloadedIndices.current.add(index);

      console.log(`📥 Preloading photo at index ${index}:`, photo.originalFilename);
    });
  }, [photos, currentIndex, enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      preloadedImages.current.forEach((img) => {
        img.src = "";
      });
      preloadedImages.current.clear();
      preloadedIndices.current.clear();
    };
  }, []);

  return {};
};
